import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TableName } from 'modules/core/enums';
import { costFormatter } from 'modules/core/utils';
import { useArraFinanceBackAdds } from 'modules/core/hooks/useArraFinanceBackAdds';
import { FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES } from 'modules/RACAffordabilityEngine/const/fieldNames.const';
import { getRegionControls } from 'store/selectors/RACAffordabilityEngine.selector';
import { getCurrentLenderControls, getLenderState } from 'store/selectors/lenders.selector';

interface IVscGapLimitResult {
  message: string;
  isInvalid: boolean;
}

interface IUseVscGapValidationResult {
  getVscOrGapLimit: (fieldName: string) => IVscGapLimitResult;
  isVscInvalid: boolean;
  isGapInvalid: boolean;
  isDisabled: (name: string) => boolean;
}

export const useVscGapValidation = (tableName: TableName, vsc: number, gap: number): IUseVscGapValidationResult => {
  const { t } = useTranslation();
  const regionControls = useSelector(getRegionControls);
  const lenderControls = useSelector(getCurrentLenderControls);
  const lenderState = useSelector(getLenderState);
  const dealSourceData = lenderState?.dealSourceData;
  const isLender = tableName === TableName.LENDER;
  const { isArraFinance } = useArraFinanceBackAdds();

  const getVscLimit = useCallback(
    (dealSourceData: any, lenderControls: any, regionControls: any): number => {
      if (!isLender) {
        return regionControls?.vscLimitBelow ?? 0;
      }

      if (
        !isArraFinance &&
        dealSourceData?.finance_details?.override_vsc > 0 &&
        dealSourceData?.finance_details?.override_vsc < lenderControls?.maxVSC
      ) {
        return dealSourceData.finance_details.override_vsc;
      }

      return lenderControls?.maxVSC ?? 0;
    },
    [isLender, isArraFinance],
  );
  const vscLimit = getVscLimit(dealSourceData, lenderControls, regionControls);
  const gapLimitValue = isLender ? lenderControls?.maxGap : regionControls?.gapLimit;
  const isVscInvalid = !!vscLimit && vsc > vscLimit;
  const isGapInvalid = !!gapLimitValue && gap > gapLimitValue;

  const getVscOrGapLimit = useCallback(
    (fieldName: string): IVscGapLimitResult => {
      let message = '';
      const vscLimitBelow = getVscLimit(dealSourceData, lenderControls, regionControls);
      const gapLimit = isLender ? lenderControls?.maxGap : regionControls?.gapLimit;

      if (fieldName === FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.VSC && !!vscLimitBelow) {
        const isInvalid = vsc > vscLimitBelow;

        if ((isInvalid && isLender) || !isLender) {
          const maxFormatted = costFormatter(vscLimitBelow, false);

          if (isLender) {
            message = t('components.RACAffordabilityEngine.vscAndGapLimits.backEndLimitMessage', {
              maxBackEnd: maxFormatted,
            });
          } else {
            message = t('components.RACAffordabilityEngine.vscAndGapLimits.vscLimitMessage', {
              maxVSC: maxFormatted,
            });
          }
        }

        return { message, isInvalid };
      }

      if (fieldName === FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.GAP && !!gapLimit) {
        const isInvalid = gap > gapLimit;

        message = t('components.RACAffordabilityEngine.vscAndGapLimits.gapLimitMessage', {
          maxGap: costFormatter(gapLimit, false),
        });

        return { message, isInvalid };
      }

      return { message, isInvalid: false };
    },
    [isLender, getVscLimit, dealSourceData, lenderControls, regionControls, vsc, gap, t],
  );

  const isDisabled = useCallback(
    (name: string): boolean => {
      if (name === FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.GAP) {
        return isLender ? !lenderControls?.maxGap : !regionControls?.gapLimit;
      }

      if (name === FRONT_AND_BACK_ENDS_ADDS_FIELD_NAMES.VSC) {
        return isLender ? !lenderControls?.maxVSC : false;
      }

      return false;
    },
    [isLender, lenderControls?.maxGap, lenderControls?.maxVSC, regionControls?.gapLimit],
  );

  return {
    getVscOrGapLimit,
    isVscInvalid,
    isGapInvalid,
    isDisabled,
  };
};
